import {
  hexToRgba,
  isSameHexColor,
  normalizeHex,
  resolveThemeColorTargets,
  // toOpaqueHex,
} from "./customPrimaryColor";

/** 与 shared-theme 中 ThemeColorMode 一致 */
type ThemeColorMode = "light" | "dark";

/** 字体色默认值（不区分品牌，浅/深隔离） */
const DEFAULT_TEXT_BY_MODE: Record<ThemeColorMode, string> = {
  light: "#1E2023", // rgba(30, 32, 35, 1)
  dark: "#FFFFFFF5", // rgba(255, 255, 255, 0.96)
};

const CUSTOM_TEXT_STORAGE_KEY = "app-custom-text-colors";

export type CustomTextColors = Partial<Record<ThemeColorMode, string>>;

/** 按模式读取默认字体色 */
export const getDefaultTextByMode = (mode: ThemeColorMode): string =>
  DEFAULT_TEXT_BY_MODE[mode];

export const isDefaultTextColor = (mode: ThemeColorMode, color: string) =>
  isSameHexColor(color, getDefaultTextByMode(mode));

const parseStoredCustomTextColors = (raw: string | null): CustomTextColors => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as CustomTextColors;
    return {
      light: parsed.light ? normalizeHex(parsed.light) || undefined : undefined,
      dark: parsed.dark ? normalizeHex(parsed.dark) || undefined : undefined,
    };
  } catch {
    return {};
  }
};

export const readStoredCustomTextColors = (): CustomTextColors =>
  parseStoredCustomTextColors(localStorage.getItem(CUSTOM_TEXT_STORAGE_KEY));

export const writeStoredCustomTextColor = (
  mode: ThemeColorMode,
  color: string | null
) => {
  const next = readStoredCustomTextColors();
  if (color) {
    next[mode] = color;
  } else {
    delete next[mode];
  }
  localStorage.setItem(CUSTOM_TEXT_STORAGE_KEY, JSON.stringify(next));
};

/** 由基础字体色生成各透明度阶梯 */
const writeTextColorVars = (
  target: HTMLElement,
  mode: ThemeColorMode,
  color: string
) => {
  target.style.setProperty(`--custom-text-primary-${mode}`, color);
  target.style.setProperty(
    `--custom-text-secondary-${mode}`,
    hexToRgba(color, 0.85)
  );
  target.style.setProperty(
    `--custom-text-tertiary-${mode}`,
    hexToRgba(color, 0.65)
  );
  target.style.setProperty(
    `--custom-text-disabled-${mode}`,
    hexToRgba(color, 0.45)
  );
  target.style.setProperty(
    `--custom-text-quaternary-${mode}`,
    hexToRgba(color, 0.25)
  );
  target.style.setProperty(
    `--custom-text-subtle-${mode}`,
    hexToRgba(color, 0.04)
  );

  if (mode === "light") {
    target.style.setProperty(
      `--custom-text-hover-${mode}`,
      hexToRgba(color, 0.15)
    );
  }
};

export const clearCustomTextCssVars = (
  mode: ThemeColorMode,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    target.style.removeProperty(`--custom-text-primary-${mode}`);
    target.style.removeProperty(`--custom-text-secondary-${mode}`);
    target.style.removeProperty(`--custom-text-tertiary-${mode}`);
    target.style.removeProperty(`--custom-text-disabled-${mode}`);
    target.style.removeProperty(`--custom-text-quaternary-${mode}`);
    target.style.removeProperty(`--custom-text-subtle-${mode}`);
    target.style.removeProperty(`--custom-text-hover-${mode}`);
  });
};

export const applyTextColor = (
  mode: ThemeColorMode,
  color: string,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    writeTextColorVars(target, mode, color);
  });
};

/** 读取某模式下已生效的字体色（优先 localStorage） */
export const readAppliedTextColor = (mode: ThemeColorMode) => {
  const stored = readStoredCustomTextColors()[mode];
  if (stored) return stored;
  return getDefaultTextByMode(mode);
};

/**
 * 将 localStorage 中的自定义字体色恢复到 DOM。
 * 浅色 / 深色分别写入，互不影响。
 */
export const restoreStoredCustomTextColors = (element?: HTMLElement) => {
  const stored = readStoredCustomTextColors();
  resolveThemeColorTargets(element).forEach((target) => {
    (["light", "dark"] as const).forEach((mode) => {
      const color = stored[mode];
      if (color) {
        writeTextColorVars(target, mode, color);
      }
    });
  });
};
